import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaAtom, FaLightbulb, FaCalculator } from 'react-icons/fa';

const NewtonsSecondLawCalculator = () => {
  const [mass, setMass] = useState('');
  const [acceleration, setAcceleration] = useState('');
  const [force, setForce] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateForce = () => {
    if (mass && acceleration) {
      const calculatedForce = parseFloat(mass) * parseFloat(acceleration);
      setForce(calculatedForce.toFixed(2));
      setShowGraph(true);
    }
  };

  const generateGraphData = () => {
    if (!mass || !acceleration) return null;

    const massValue = parseFloat(mass);
    const accelerationValue = parseFloat(acceleration);

    const series = [
      {
        name: 'Force (N)',
        data: Array.from({ length: 10 }, (_, i) => {
          const currentMass = massValue + (i - 5);
          return currentMass * accelerationValue;
        }),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        height: 350,
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
      },
      xaxis: {
        type: 'numeric',
        title: {
          text: 'Mass (kg)',
        },
      },
      yaxis: {
        title: {
          text: 'Force (N)',
        },
      },
      title: {
        text: 'Force vs Mass Relationship',
        align: 'center',
      },
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100">
      <Helmet>
        <title>Newton's Second Law Calculator | F=ma Physics Tool</title>
        <meta name="description" content="Calculate force using Newton's Second Law (F=ma). Interactive calculator with graph visualization for understanding the relationship between force, mass, and acceleration." />
        <meta name="keywords" content="newton's second law calculator, f=ma calculator, physics calculator, force calculator, mass acceleration calculator" />
        <meta property="og:title" content="Newton's Second Law Calculator | F=ma Physics Tool" />
        <meta property="og:description" content="Calculate force using Newton's Second Law (F=ma). Interactive calculator with graph visualization for understanding the relationship between force, mass, and acceleration." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Newton's Second Law Calculator | F=ma Physics Tool" />
        <meta name="twitter:description" content="Calculate force using Newton's Second Law (F=ma). Interactive calculator with graph visualization for understanding the relationship between force, mass, and acceleration." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/newtons-second-law" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">
            <FaAtom />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Newton's Second Law Calculator</h1>
          <p className="text-xl text-gray-600">Calculate Force using F=ma</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Mass (kg)</label>
                <input
                  type="number"
                  value={mass}
                  onChange={(e) => setMass(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Acceleration (m/s²)</label>
                <input
                  type="number"
                  value={acceleration}
                  onChange={(e) => setAcceleration(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
              <div>
                <button
                  onClick={calculateForce}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Force
                </button>
              </div>
              {force && (
                <div className="mt-4 p-4 bg-green-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-green-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-green-800">Result</h3>
                      <p className="text-green-800">Force = {force} N</p>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      According to Newton's Second Law (F = ma):
                      <br />
                      Force (F) = Mass (m) × Acceleration (a)
                      <br />
                      <br />
                      In this case:
                      <br />
                      Mass = {mass} kg
                      <br />
                      Acceleration = {acceleration} m/s²
                      <br />
                      <br />
                      Therefore, Force = {mass} × {acceleration} = {force} N
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Force vs Mass Relationship</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={350}
              />
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the mass of the object in kilograms (kg)</li>
            <li>Enter the acceleration in meters per second squared (m/s²)</li>
            <li>Click "Calculate Force" to get the result</li>
            <li>View the interactive graph showing the relationship between force and mass</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Instant Calculations</h3>
              <p>Get immediate results for force calculations using F=ma</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Graph</h3>
              <p>Visualize the relationship between force and mass with our dynamic graph</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">User-Friendly Interface</h3>
              <p>Simple and intuitive design for easy calculations</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Perfect for students and educators to understand Newton's Second Law</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is Newton's Second Law?</h3>
              <p className="text-gray-600">
                Newton's Second Law states that the acceleration of an object is directly proportional to the net force acting on it and inversely proportional to its mass. Mathematically, it's expressed as F = ma, where F is force, m is mass, and a is acceleration.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                For accurate calculations:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Mass should be in kilograms (kg)</li>
                  <li>Acceleration should be in meters per second squared (m/s²)</li>
                  <li>The result will be in Newtons (N)</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the graph work?</h3>
              <p className="text-gray-600">
                The graph shows how force changes with different mass values while keeping the acceleration constant. It helps visualize the linear relationship between force and mass as described by Newton's Second Law.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                Newton's Second Law is fundamental in many areas, including:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Engineering and mechanics</li>
                  <li>Vehicle dynamics and safety</li>
                  <li>Sports science and biomechanics</li>
                  <li>Aerospace and rocket science</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Students understand the relationship between force, mass, and acceleration</li>
                  <li>Engineers quickly calculate forces in mechanical systems</li>
                  <li>Teachers demonstrate Newton's Second Law in physics classes</li>
                  <li>Anyone needing to calculate forces in everyday situations</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewtonsSecondLawCalculator;
